import { ThemeOptions } from '@mui/material/styles';
import { baseComponents, baseSpacing, baseTypography } from './baseTheme';

/**
 * Pro1 - Modern Professional Light
 * A refined, contemporary light theme with an indigo/teal accent,
 * soft shadows and generous rounding for a polished, non-simplistic look.
 * Primary: Indigo (#4f46e5)
 * Secondary: Teal (#0d9488)
 */
export const pro1Theme: ThemeOptions = {
    palette: {
        mode: 'light',
        primary: {
            main: '#4f46e5',
            light: '#6366f1',
            dark: '#4338ca',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#0d9488',
            light: '#14b8a6',
            dark: '#0f766e',
            contrastText: '#ffffff',
        },
        background: {
            default: '#f4f6fb',
            paper: '#ffffff',
        },
        text: {
            primary: '#1e293b',
            secondary: '#64748b',
        },
        divider: '#e2e8f0',
        error: {
            main: '#dc2626',
        },
        warning: {
            main: '#d97706',
        },
        info: {
            main: '#2563eb',
        },
        success: {
            main: '#16a34a',
        },
    },
    typography: {
        ...baseTypography,
        fontFamily: [
            '"Inter"',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
        ].join(','),
        h1: { fontSize: '1.85rem', fontWeight: 700, letterSpacing: '-0.02em' },
        h2: { fontSize: '1.55rem', fontWeight: 700, letterSpacing: '-0.02em' },
        h3: { fontSize: '1.3rem', fontWeight: 700, letterSpacing: '-0.01em' },
        h4: { fontSize: '1.15rem', fontWeight: 600, letterSpacing: '-0.01em' },
        h5: { fontSize: '1rem', fontWeight: 600 },
        h6: { fontSize: '0.9rem', fontWeight: 600 },
    },
    spacing: baseSpacing,
    shape: {
        borderRadius: 10,
    },
    components: {
        ...baseComponents,
        MuiButton: {
            defaultProps: { size: 'small', disableElevation: true },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    minHeight: 30,
                    padding: '5px 14px',
                    borderRadius: 8,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    border: '1px solid #eef1f6',
                    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.06)',
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: { padding: '14px 16px' },
                title: { fontSize: '0.95rem', fontWeight: 700 },
                subheader: { fontSize: '0.75rem' },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: { backgroundImage: 'none' },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#1e293b',
                    backgroundImage: 'linear-gradient(180deg, #1e293b 0%, #312e81 100%)',
                    borderRight: 'none',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#ffffff',
                    color: '#1e293b',
                    boxShadow: '0 1px 0 rgba(16, 24, 40, 0.06), 0 2px 8px rgba(16, 24, 40, 0.04)',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '6px 10px',
                    fontSize: '0.8125rem',
                    borderColor: '#eef1f6',
                },
                head: {
                    fontWeight: 700,
                    padding: '8px 10px',
                    backgroundColor: '#f8fafc',
                    color: '#475569',
                },
            },
        },
    },
};
